import * as Haptics from 'expo-haptics';
import React from 'react';
import { Platform, Pressable, type GestureResponderEvent, type PressableProps } from 'react-native';
import { useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';
import { cn } from '@/utils/cn';
import { AnimatedPressable } from './animated';

const PRESS_SPRING = { damping: 18, stiffness: 320, mass: 0.6 };

export interface PressableScaleProps extends PressableProps {
  className?: string;
  /** Escala aplicada ao pressionar (padrão 0.97) */
  pressScale?: number;
  /** Dispara um leve haptic no iOS ao pressionar */
  haptic?: boolean;
  children?: React.ReactNode;
}

/**
 * Pressable com feedback de escala + haptic opcional.
 * Base de interação para cards, botões e itens de lista.
 *
 * No nativo, qualquer nó do Reanimated no caminho do toque (AnimatedPressable
 * ou AnimatedView filho) engole os eventos de press — então o nativo usa um
 * Pressable puro com o feedback via `active:scale-[0.97]` do NativeWind,
 * o mesmo padrão dos Pressables com `active:` que funcionam no app.
 */
export function PressableScale({
  pressScale = 0.97,
  haptic = false,
  onPressIn,
  onPressOut,
  onPress,
  className,
  style,
  children,
  ...props
}: PressableScaleProps) {
  const scale = useSharedValue(1);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const handlePress = (event: GestureResponderEvent) => {
    if (haptic && process.env.EXPO_OS === 'ios') {
      void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    onPress?.(event);
  };

  if (Platform.OS === 'web') {
    const handlePressIn = (event: GestureResponderEvent) => {
      scale.value = withSpring(pressScale, PRESS_SPRING);
      onPressIn?.(event);
    };

    const handlePressOut = (event: GestureResponderEvent) => {
      scale.value = withSpring(1, PRESS_SPRING);
      onPressOut?.(event);
    };

    return (
      <AnimatedPressable
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onPress={handlePress}
        className={className}
        style={[animatedStyle, style as never]}
        {...props}
      >
        {children}
      </AnimatedPressable>
    );
  }

  return (
    <Pressable
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      onPress={handlePress}
      className={cn('active:scale-[0.97]', className)}
      style={style}
      {...props}
    >
      {children}
    </Pressable>
  );
}
